/**
 * Heuristische Vorbefüllung der Bewertungstexte einer Stellungnahme.
 *
 * Diese Funktionen leiten aus den **strukturierten** Wizard-Daten (Score,
 * Financial, Dimensions, BG, Business) Vorschlagstexte ab, die der Berater
 * im AssessmentEditor übernimmt, anpasst oder verwirft.
 *
 * **Wichtig (Art. 22 DSGVO):** Diese Texte sind ENTWÜRFE, keine
 * automatisiert generierten Stellungnahmen. Der Berater verantwortet
 * jeden Text — die UI muss das deutlich kennzeichnen.
 *
 * **Pure Functions:** keine Seiteneffekte, keine IO, kein `new Date()`
 * ohne Injection. `currentDate` ist als Parameter erforderlich, damit
 * Tests deterministisch laufen.
 *
 * **Sprachregeln** (siehe `CLAUDE.md`):
 *  - Sie-Form, behördentauglich, sachlich.
 *  - Keine Marketing-Floskeln („herausragend", „spitze", „ganzheitlich").
 *  - Konkrete Zahlen statt Adjektiven, wo möglich.
 *  - Sentinel-Phrase „Vorschlag nicht möglich — …" wenn Datenbasis fehlt.
 */

import type { MoneyCents } from '../schema/brands.js';
import type {
  ActivityData,
  DimensionData,
  DimensionKey,
  DimensionPayload,
  HealthData,
  IdentityData,
  PrivateData,
  ResourcesData,
  StyleData,
} from '../schema/dimensionData.js';
import type { FinancialDataset } from '../schema/financialDataset.js';
import type { ScoreResult, TrafficLight } from '../schema/score.js';
import { computeBgBedarf } from '../bg/computeBgBedarf.js';
import { formatEuro, formatEuroPerHour, formatGermanDate, formatPercent } from './formatters.js';
import { TRAFFIC_LABEL_DE, VERDICT_BY_TRAFFIC } from './types.js';
import { inferBranchHourlyFloor } from './branchHourlyFloor.js';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface HeuristicsBusinessProfile {
  readonly branch: string;
  readonly legalForm: string;
  readonly startDate: Date;
}

export interface HeuristicsInput {
  readonly score: ScoreResult;
  readonly financial: FinancialDataset;
  readonly dimensions: ReadonlyArray<DimensionData>;
  readonly business: HeuristicsBusinessProfile;
}

export interface DimensionTextPair {
  readonly short: string;
  readonly detail: string;
}

export interface HeuristicsOutput {
  // Pflicht-Texte
  readonly verdictLongParagraph: string;
  readonly verdictReasoning: string;
  readonly recommendationDetailed: string;
  readonly constraintDiscussion: string;
  readonly riskIndicators: string;
  readonly strengthIndicators: string;
  // Optional-Texte
  readonly revenueAssessment: string;
  readonly costsAssessment: string;
  readonly profitAssessment: string;
  readonly hourlyRateAssessment: string;
  readonly liquidityAssessment: string;
  readonly runwayAssessment: string;
  readonly utilizationAssessment: string;
  readonly gapInterpretation: string;
  readonly conditionsParagraph: string;
  readonly reevaluationProposal: string;
  // Pro Dimension
  readonly dimensionTexts: Readonly<Record<DimensionKey, DimensionTextPair>>;
}

const NOT_POSSIBLE_BWA = 'Vorschlag nicht möglich — BWA-Daten fehlen.';
const NOT_POSSIBLE_HOURS = 'Vorschlag nicht möglich — Stundenangabe fehlt.';
const NOT_POSSIBLE_LIQ = 'Vorschlag nicht möglich — Liquiditäts-Stichtag fehlt.';
const CONFIDENCE_DISCLAIMER =
  'Hinweis: Die Datenbasis ist nicht abschließend; eine Nachforderung weiterer Unterlagen wird empfohlen.';
const CONFIDENCE_DISCLAIMER_THRESHOLD = 60;

/**
 * Hauptfunktion — leitet alle Bewertungstexte aus den strukturierten Daten ab.
 *
 * @param input  Score, Financial, Dimensions, Business
 * @param currentDate  Stichdatum für Re-Evaluation; default `new Date()`. In
 *                     Tests immer explizit setzen, sonst sind die Outputs
 *                     nicht deterministisch.
 */
export function buildHeuristicAssessments(
  input: HeuristicsInput,
  currentDate: Date = new Date(),
): HeuristicsOutput {
  const ctx = buildContext(input, currentDate);

  const dimensionTexts = buildDimensionTexts(ctx);

  return {
    // Pflicht
    verdictLongParagraph: buildVerdictLongParagraph(ctx),
    verdictReasoning: buildVerdictReasoning(ctx),
    recommendationDetailed: buildRecommendationDetailed(ctx),
    constraintDiscussion: buildConstraintDiscussion(ctx),
    riskIndicators: buildRiskIndicators(ctx),
    strengthIndicators: buildStrengthIndicators(ctx),
    // Optional / Kennzahlen
    revenueAssessment: buildRevenueAssessment(ctx),
    costsAssessment: buildCostsAssessment(ctx),
    profitAssessment: buildProfitAssessment(ctx),
    hourlyRateAssessment: buildHourlyRateAssessment(ctx),
    liquidityAssessment: buildLiquidityAssessment(ctx),
    runwayAssessment: buildRunwayAssessment(ctx),
    utilizationAssessment: buildUtilizationAssessment(ctx),
    gapInterpretation: buildGapInterpretation(ctx),
    conditionsParagraph: buildConditionsParagraph(ctx),
    reevaluationProposal: buildReevaluationProposal(ctx),
    dimensionTexts,
  };
}

// ---------------------------------------------------------------------------
// Aggregations-Kontext — einmal berechnet, dann von allen Builders konsumiert
// ---------------------------------------------------------------------------

interface HeuristicsContext {
  readonly input: HeuristicsInput;
  readonly currentDate: Date;
  readonly traffic: TrafficLight;
  readonly trafficLabel: string;
  readonly totalScore: number;
  readonly confidence: number;
  readonly hasBwa: boolean;
  readonly months: number;
  readonly avgRevenueCents: number;
  readonly avgCostsCents: number;
  readonly avgProfitCents: number;
  readonly revenueCv: number; // Variationskoeffizient, 0..∞
  readonly hours: number | null;
  readonly hourlyRateEuro: number | null;
  readonly bgBedarfCents: number;
  readonly bgGapCents: number; // positiv = Lücke
  readonly bgCoveragePct: number; // wieviel % vom Bedarf gedeckt
  readonly liquidityCents: number | null;
  readonly burnRateCents: number;
  readonly runwayWeeks: number | null;
  readonly utilizationPct: number | null;
  readonly branchFloorEuro: number;
  readonly branchInferred: boolean;
  readonly businessAgeMonths: number;
  readonly identity: IdentityData | null;
  readonly activity: ActivityData | null;
  readonly style: StyleData | null;
  readonly resources: ResourcesData | null;
  readonly health: HealthData | null;
  readonly private_: PrivateData | null;
  readonly hasYoungChildren: boolean; // unter 15
  readonly maxClumpenSharePct: number; // Klumpenrisiko
  readonly verdictPhrase: string;
}

function buildContext(input: HeuristicsInput, currentDate: Date): HeuristicsContext {
  const { score, financial, dimensions, business } = input;

  const months = financial.monthlyAggregates.length;
  const revenues = financial.monthlyAggregates.map((m) => m.revenueCents as number);
  const costs = financial.monthlyAggregates.map((m) => m.costsCents as number);
  const avgRevenueCents = avg(revenues);
  const avgCostsCents = avg(costs);
  const avgProfitCents = Math.round(avgRevenueCents - avgCostsCents);
  const revenueCv = coefficientOfVariation(revenues);

  const hours = (financial.hourlyRate.billableHoursPerMonth as number | undefined) ?? null;
  const hourlyRateEuro = hours !== null && hours > 0 ? avgProfitCents / 100 / hours : null;

  const bgResult = computeBgBedarf(financial.bgComposition);
  const bgBedarfCents = bgResult.totalBedarfCents as number;
  const profitForCoverage = Math.max(0, avgProfitCents);
  const bgGapCents = Math.max(0, bgBedarfCents - profitForCoverage);
  const bgCoveragePct =
    bgBedarfCents > 0 ? Math.round((profitForCoverage / bgBedarfCents) * 100) : 0;

  const liquidityCents = financial.liquidity
    ? (financial.liquidity.liquidAssetsCents as number)
    : null;
  const burnRateCents = avgCostsCents > 0 ? avgCostsCents : 1;
  const runwayWeeks =
    liquidityCents !== null && liquidityCents > 0
      ? Math.round((liquidityCents / burnRateCents) * 4.33)
      : null;

  const utilizationPct = hours !== null ? (hours / 160) * 100 : null;

  const branchInfo = inferBranchHourlyFloor(business.branch);

  const businessAgeMonths = monthsBetween(business.startDate, currentDate);

  const identity = pickDimension<IdentityData>(dimensions, 'identity');
  const activity = pickDimension<ActivityData>(dimensions, 'activity');
  const style = pickDimension<StyleData>(dimensions, 'style');
  const resources = pickDimension<ResourcesData>(dimensions, 'resources');
  const health = pickDimension<HealthData>(dimensions, 'health');
  const private_ = pickDimension<PrivateData>(dimensions, 'private');

  const hasYoungChildren = financial.bgComposition.members.some(
    (m) => m.role === 'kind' && m.age < 15,
  );

  const maxClumpenSharePct =
    activity?.topClientsByRevenue.reduce((m, c) => Math.max(m, c.revenueSharePercent), 0) ?? 0;

  const traffic = score.trafficLight;
  const verdictPhrase = VERDICT_BY_TRAFFIC[traffic];

  return {
    input,
    currentDate,
    traffic,
    trafficLabel: TRAFFIC_LABEL_DE[traffic],
    totalScore: score.totalScore,
    confidence: score.totalConfidencePercent,
    hasBwa: months > 0 && (avgRevenueCents > 0 || avgCostsCents > 0),
    months,
    avgRevenueCents,
    avgCostsCents,
    avgProfitCents,
    revenueCv,
    hours,
    hourlyRateEuro,
    bgBedarfCents,
    bgGapCents,
    bgCoveragePct,
    liquidityCents,
    burnRateCents,
    runwayWeeks,
    utilizationPct,
    branchFloorEuro: branchInfo.floorEuroPerHour,
    branchInferred: branchInfo.inferred,
    businessAgeMonths,
    identity,
    activity,
    style,
    resources,
    health,
    private_,
    hasYoungChildren,
    maxClumpenSharePct,
    verdictPhrase,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function avg(xs: ReadonlyArray<number>): number {
  if (xs.length === 0) return 0;
  let sum = 0;
  for (const x of xs) sum += x;
  return Math.round(sum / xs.length);
}

function coefficientOfVariation(xs: ReadonlyArray<number>): number {
  if (xs.length < 2) return 0;
  const mean = xs.reduce((s, x) => s + x, 0) / xs.length;
  if (mean === 0) return 0;
  const variance = xs.reduce((s, x) => s + (x - mean) ** 2, 0) / xs.length;
  const stdDev = Math.sqrt(variance);
  return stdDev / Math.abs(mean);
}

function monthsBetween(from: Date, to: Date): number {
  const ms = to.getTime() - from.getTime();
  if (!Number.isFinite(ms) || ms < 0) return 0;
  // 30.44 Tage / Monat im Mittel
  return Math.floor(ms / (1000 * 60 * 60 * 24 * 30.44));
}

function pickDimension<T extends DimensionPayload>(
  dimensions: ReadonlyArray<DimensionData>,
  key: DimensionKey,
): T | null {
  const found = dimensions.find((d) => d.payload.dimension === key);
  return (found?.payload as T | undefined) ?? null;
}

/** Hängt den Konfidenz-Disclaimer an, wenn confidence < Schwelle. */
function withConfidenceDisclaimer(text: string, ctx: HeuristicsContext): string {
  if (ctx.confidence < CONFIDENCE_DISCLAIMER_THRESHOLD) {
    return `${text}\n\n${CONFIDENCE_DISCLAIMER}`;
  }
  return text;
}

function bullet(line: string): string {
  return `· ${line}`;
}

function joinBullets(lines: ReadonlyArray<string>, fallback: string): string {
  if (lines.length === 0) return fallback;
  return lines.join('\n');
}

function addMonths(date: Date, months: number): Date {
  const next = new Date(date.getTime());
  next.setUTCMonth(next.getUTCMonth() + months);
  return next;
}

function profitVsCostsRatio(ctx: HeuristicsContext): number | null {
  if (ctx.avgRevenueCents <= 0) return null;
  return ctx.avgCostsCents / ctx.avgRevenueCents;
}

// ---------------------------------------------------------------------------
// Optional: Kennzahlen-Bewertungen (Einzeiler)
// ---------------------------------------------------------------------------

function buildRevenueAssessment(ctx: HeuristicsContext): string {
  if (!ctx.hasBwa) return NOT_POSSIBLE_BWA;
  const monthly = formatEuro(ctx.avgRevenueCents as MoneyCents);
  const periodLabel =
    ctx.months >= 12
      ? 'über 12 Monate'
      : ctx.months >= 6
        ? `über ${String(ctx.months)} Monate`
        : `auf ${String(ctx.months)} Monatsdaten`;
  if (ctx.months < 2) {
    return `Durchschnittlicher Monatsumsatz ${monthly} (${periodLabel}). Aussagen zur Stabilität sind aus einem einzelnen Monat nicht möglich.`;
  }
  if (ctx.revenueCv < 0.15) {
    return `Durchschnittlicher Monatsumsatz ${monthly}, ${periodLabel} stabil (Schwankung unter 15 %).`;
  }
  if (ctx.revenueCv < 0.3) {
    return `Durchschnittlicher Monatsumsatz ${monthly}, ${periodLabel} mit moderater Schwankung von ${formatPercent(ctx.revenueCv * 100)}.`;
  }
  return `Durchschnittlicher Monatsumsatz ${monthly}, ${periodLabel} mit hoher Schwankung von ${formatPercent(ctx.revenueCv * 100)}; Saisonalität prüfen.`;
}

function buildCostsAssessment(ctx: HeuristicsContext): string {
  if (!ctx.hasBwa) return NOT_POSSIBLE_BWA;
  const monthly = formatEuro(ctx.avgCostsCents as MoneyCents);
  const ratio = profitVsCostsRatio(ctx);
  if (ratio === null) {
    return `Durchschnittliche Monatsausgaben ${monthly}.`;
  }
  if (ratio < 0.4) {
    return `Durchschnittliche Monatsausgaben ${monthly}, ${formatPercent(ratio * 100)} vom Umsatz — Kostenstruktur schlank.`;
  }
  if (ratio < 0.7) {
    return `Durchschnittliche Monatsausgaben ${monthly}, ${formatPercent(ratio * 100)} vom Umsatz — branchenübliche Kostenstruktur.`;
  }
  return `Durchschnittliche Monatsausgaben ${monthly}, ${formatPercent(ratio * 100)} vom Umsatz — Kostenanteil hoch; Optimierungspotential prüfen.`;
}

function buildProfitAssessment(ctx: HeuristicsContext): string {
  if (!ctx.hasBwa) return NOT_POSSIBLE_BWA;
  const profit = formatEuro(ctx.avgProfitCents as MoneyCents);
  const bedarf = formatEuro(ctx.bgBedarfCents as MoneyCents);
  if (ctx.avgProfitCents <= 0) {
    return `Durchschnittlicher Monatsgewinn ${profit} — die Tätigkeit deckt aktuell nicht ihre Kosten. BG-Bedarf ${bedarf} bleibt vollständig ungedeckt.`;
  }
  if (ctx.bgCoveragePct >= 100) {
    const overshoot = ctx.bgCoveragePct - 100;
    return `Durchschnittlicher Monatsgewinn ${profit} — deckt den BG-Bedarf von ${bedarf} vollständig (Überdeckung ${formatPercent(overshoot)}).`;
  }
  if (ctx.bgCoveragePct >= 80) {
    return `Durchschnittlicher Monatsgewinn ${profit} — deckt den BG-Bedarf von ${bedarf} zu ${formatPercent(ctx.bgCoveragePct)}; geringfügige ergänzende Hilfebedürftigkeit.`;
  }
  if (ctx.bgCoveragePct >= 50) {
    return `Durchschnittlicher Monatsgewinn ${profit} — deckt den BG-Bedarf von ${bedarf} zu ${formatPercent(ctx.bgCoveragePct)}; substantielle ergänzende Hilfebedürftigkeit fortbestehend.`;
  }
  return `Durchschnittlicher Monatsgewinn ${profit} — deckt den BG-Bedarf von ${bedarf} nur zu ${formatPercent(ctx.bgCoveragePct)}; Tragfähigkeitslücke groß.`;
}

function buildHourlyRateAssessment(ctx: HeuristicsContext): string {
  if (!ctx.hasBwa) return NOT_POSSIBLE_BWA;
  if (ctx.hours === null) return NOT_POSSIBLE_HOURS;
  if (ctx.hourlyRateEuro === null) return NOT_POSSIBLE_HOURS;
  const rate = formatEuroPerHour(ctx.hourlyRateEuro);
  const floor = formatEuroPerHour(ctx.branchFloorEuro);
  const branchHint = ctx.branchInferred
    ? `für die Branche ${ctx.input.business.branch}`
    : 'als allgemeine Untergrenze';
  if (ctx.hourlyRateEuro <= 0) {
    return `Bereinigter Stundensatz ${rate} — die Tätigkeit deckt nicht die eigenen Kosten. Branchenuntergrenze liegt bei ${floor} ${branchHint}.`;
  }
  if (ctx.hourlyRateEuro >= ctx.branchFloorEuro * 1.2) {
    return `Bereinigter Stundensatz ${rate} liegt deutlich über der Branchenuntergrenze von ${floor} ${branchHint}; marktkonform.`;
  }
  if (ctx.hourlyRateEuro >= ctx.branchFloorEuro) {
    return `Bereinigter Stundensatz ${rate} ${branchHint} entspricht in etwa der Branchenuntergrenze von ${floor}; marktkonform.`;
  }
  const gap = Math.round((1 - ctx.hourlyRateEuro / ctx.branchFloorEuro) * 100);
  return `Bereinigter Stundensatz ${rate} liegt ${formatPercent(gap)} unter der Branchenuntergrenze von ${floor} ${branchHint}; Anhebung empfohlen.`;
}

function buildLiquidityAssessment(ctx: HeuristicsContext): string {
  if (ctx.liquidityCents === null) return NOT_POSSIBLE_LIQ;
  const liq = formatEuro(ctx.liquidityCents as MoneyCents);
  const burn = formatEuro(ctx.burnRateCents as MoneyCents);
  if (ctx.liquidityCents <= 0) {
    return `Liquide Mittel ${liq} — keine Liquiditätsreserve vorhanden.`;
  }
  if (ctx.runwayWeeks === null) {
    return `Liquide Mittel ${liq}; monatliche Burn-Rate ${burn}.`;
  }
  return `Liquide Mittel ${liq} decken die Burn-Rate von ${burn} für rund ${String(ctx.runwayWeeks)} Wochen.`;
}

function buildRunwayAssessment(ctx: HeuristicsContext): string {
  if (ctx.avgProfitCents > 0) {
    return 'Reichweite nicht relevant — positiver Cashflow trägt die Tätigkeit.';
  }
  if (ctx.liquidityCents === null) {
    return NOT_POSSIBLE_LIQ;
  }
  if (ctx.runwayWeeks === null || ctx.runwayWeeks <= 0) {
    return 'Reichweite null — keine Liquiditätsreserve, sofortige Maßnahmen erforderlich.';
  }
  if (ctx.runwayWeeks < 6) {
    return `Reichweite rund ${String(ctx.runwayWeeks)} Wochen — kritisch (unter 6 Wochen).`;
  }
  if (ctx.runwayWeeks < 12) {
    return `Reichweite rund ${String(ctx.runwayWeeks)} Wochen — angespannt (6–12 Wochen).`;
  }
  return `Reichweite rund ${String(ctx.runwayWeeks)} Wochen — komfortabel (über 12 Wochen).`;
}

function buildUtilizationAssessment(ctx: HeuristicsContext): string {
  if (ctx.hours === null || ctx.utilizationPct === null) return NOT_POSSIBLE_HOURS;
  const hoursLabel = `${String(ctx.hours)} h/Monat`;
  if (ctx.utilizationPct >= 90) {
    return `Auslastung ${hoursLabel} (${formatPercent(ctx.utilizationPct)} der Vollzeit-Referenz von 160 h) — Vollauslastung erreicht.`;
  }
  if (ctx.utilizationPct >= 60) {
    return `Auslastung ${hoursLabel} (${formatPercent(ctx.utilizationPct)} der Vollzeit-Referenz von 160 h) — solider Bereich.`;
  }
  if (ctx.utilizationPct >= 30) {
    return `Auslastung ${hoursLabel} (${formatPercent(ctx.utilizationPct)} der Vollzeit-Referenz von 160 h) — ausbaufähig; Akquise verstärken.`;
  }
  return `Auslastung ${hoursLabel} (${formatPercent(ctx.utilizationPct)} der Vollzeit-Referenz von 160 h) — niedrig; Akquise und Geschäftsmodell überprüfen.`;
}

function buildGapInterpretation(ctx: HeuristicsContext): string {
  // Identische Logik wie buildPlaceholders.ts Zeile 222-227, hier expliziert.
  if (ctx.bgGapCents > 0) return 'nicht in der Lage ist';
  if (ctx.avgProfitCents > ctx.bgBedarfCents) return 'in der Lage ist';
  return 'gerade in der Lage ist';
}

// ---------------------------------------------------------------------------
// Pflicht-Texte
// ---------------------------------------------------------------------------

function buildVerdictLongParagraph(ctx: HeuristicsContext): string {
  const scoreInfo = `(Score ${String(ctx.totalScore)}/100, Ampel ${ctx.trafficLabel})`;
  const firstSentence = `Die selbständige Tätigkeit weist eine ${verdictPredicate(ctx.traffic)} auf ${scoreInfo}.`;
  const gapSentence =
    ctx.bgGapCents > 0
      ? `Die monatliche Tragfähigkeitslücke beträgt ${formatEuro(ctx.bgGapCents as MoneyCents)}.`
      : 'Der erwirtschaftete Gewinn deckt den Bedarf der Bedarfsgemeinschaft.';
  const firstParagraph = `${firstSentence} ${gapSentence}`;

  const causeParagraph = `Hauptursache ist ${identifyMainCause(ctx)}.`;

  return `${firstParagraph}\n\n${causeParagraph}`;
}

function verdictPredicate(traffic: TrafficLight): string {
  switch (traffic) {
    case 'green':
      return 'gegebene Tragfähigkeit';
    case 'yellow':
      return 'eingeschränkte Tragfähigkeit';
    case 'red':
      return 'aktuell nicht gegebene Tragfähigkeit';
  }
}

function identifyMainCause(ctx: HeuristicsContext): string {
  // Wir wählen die schwächste Dimension als Hauptursache.
  const ds = ctx.input.score.dimensionScores;
  if (ds.length === 0) {
    return 'die noch unvollständige Datenlage';
  }
  const weakest = [...ds].sort((a, b) => (a.score as number) - (b.score as number))[0];
  if (!weakest) return 'die unklare Datenlage';
  const dimensionLabel = DIMENSION_LABEL_DE[weakest.dimension];
  if (ctx.bgGapCents > 0 && weakest.dimension === 'resources') {
    return `eine unzureichende Ergebnislage in der Dimension ${dimensionLabel} (Score ${String(weakest.score)}/100), die zu einer ungedeckten BG-Lücke von ${formatEuro(ctx.bgGapCents as MoneyCents)} führt`;
  }
  return `die Dimension ${dimensionLabel} mit einem Score von ${String(weakest.score)}/100`;
}

const DIMENSION_LABEL_DE: Readonly<Record<DimensionKey, string>> = {
  identity: 'Identität & Qualifikation',
  activity: 'Aktivität & Geschäftsmodell',
  style: 'Stil & Marktposition',
  resources: 'Ressourcen & Finanzen',
  health: 'Gesundheit & Resilienz',
  private: 'Privates Umfeld & Bedarfsgemeinschaft',
};

function buildVerdictReasoning(ctx: HeuristicsContext): string {
  const strengths = collectStrengthBullets(ctx);
  const risks = collectRiskBullets(ctx);

  const strengthsParagraph =
    strengths.length > 0
      ? `Stabilisierende Faktoren:\n${strengths.map(bullet).join('\n')}`
      : 'Stabilisierende Faktoren wurden in den Wizard-Daten nicht erkennbar dokumentiert.';

  const risksParagraph =
    risks.length > 0
      ? `Risiken und Schwachpunkte:\n${risks.map(bullet).join('\n')}`
      : 'Belastende Faktoren wurden in den Wizard-Daten nicht erkennbar dokumentiert.';

  return withConfidenceDisclaimer(`${strengthsParagraph}\n\n${risksParagraph}`, ctx);
}

function buildRecommendationDetailed(ctx: HeuristicsContext): string {
  if (ctx.traffic === 'green') {
    return 'Fortführung der selbständigen Tätigkeit ohne Auflagen empfohlen. Eine routinemäßige Re-Evaluation in zwölf Monaten ist ausreichend.';
  }
  if (ctx.traffic === 'red') {
    const items = [
      'Vermittlungsbemühungen in eine sozialversicherungspflichtige Beschäftigung empfohlen.',
      'Begleitend: Gespräch über die wirtschaftliche Tragfähigkeit der Selbständigkeit anhand der vorgelegten Zahlen.',
      ctx.bgGapCents > 0
        ? `Tragfähigkeitslücke ${formatEuro(ctx.bgGapCents as MoneyCents)}/Monat — ergänzende Hilfebedürftigkeit absehbar fortbestehend.`
        : 'Wirtschaftliche Lage prüfen; ergänzende Beratung empfohlen.',
      'Re-Evaluation nach drei Monaten, falls die Selbständigkeit zwischenzeitlich fortgeführt wird.',
    ];
    return numberedList(items);
  }
  // gelb
  const items: string[] = [];
  items.push('Konditionierte Fortführung der Selbständigkeit empfohlen.');
  if (ctx.utilizationPct !== null && ctx.utilizationPct < 60) {
    items.push(
      `Akquise-Strategie zur Erhöhung der Auslastung (aktuell ${formatPercent(ctx.utilizationPct)} der Vollzeit-Referenz).`,
    );
  }
  if (
    ctx.hourlyRateEuro !== null &&
    ctx.hourlyRateEuro > 0 &&
    ctx.hourlyRateEuro < ctx.branchFloorEuro
  ) {
    items.push(
      `Stundensatz-Anpassung an die Branchenuntergrenze von ${formatEuroPerHour(ctx.branchFloorEuro)}.`,
    );
  }
  if (ctx.bgGapCents > 0) {
    items.push(
      `Liquiditätsplan für die nächsten 12 Monate vorlegen; Tragfähigkeitslücke ${formatEuro(ctx.bgGapCents as MoneyCents)}/Monat dokumentieren.`,
    );
  }
  items.push('Monatliche BWA-Übermittlung an die fachkundige Stelle.');
  items.push('Re-Evaluation in sechs Monaten mit aktualisierter BWA und EKS.');
  return numberedList(items);
}

function numberedList(items: ReadonlyArray<string>): string {
  return items.map((s, i) => `${String(i + 1)}. ${s}`).join('\n');
}

function buildConstraintDiscussion(ctx: HeuristicsContext): string {
  const reasons: string[] = [];

  // Pflege im Haushalt
  if (ctx.private_?.hasCareResponsibility) {
    reasons.push('eine bestehende Pflegeverantwortung im Haushalt (§ 10 Abs. 1 Nr. 4 SGB II)');
  }
  // Junge Kinder in der BG
  if (ctx.hasYoungChildren) {
    const kids = ctx.input.financial.bgComposition.members.filter(
      (m) => m.role === 'kind' && m.age < 15,
    );
    const ages = kids.map((k) => String(k.age)).join(' und ');
    reasons.push(
      `die Erziehung von ${String(kids.length)} Kind${kids.length === 1 ? '' : 'ern'} unter 15 Jahren (${ages} Jahre) — § 10 Abs. 1 Nr. 3 SGB II`,
    );
  }
  // Gesundheitliche Einschränkung
  if (ctx.health?.conditionsAffectWork) {
    reasons.push(
      'gesundheitliche Einschränkungen, die nach Eigenangabe die Tätigkeit beeinflussen (§ 10 Abs. 1 Nr. 1 SGB II)',
    );
  }
  // Kinderbetreuung nicht gesichert
  if (ctx.private_?.childcareStatus === 'not_secured') {
    reasons.push(
      'eine nicht gesicherte Kinderbetreuung — eine Vermittlung in Vollzeit wäre derzeit nicht zumutbar',
    );
  }

  if (reasons.length === 0) {
    return 'Es sind keine Zumutbarkeitsausnahmen nach § 10 Abs. 1 SGB II erkennbar. Eine Vermittlung in eine sozialversicherungspflichtige Beschäftigung wäre grundsätzlich zumutbar, soweit die Selbständigkeit aufgegeben würde.';
  }

  const reasonText =
    reasons.length === 1
      ? `${reasons[0]}.`
      : `${reasons.slice(0, -1).join(', ')} sowie ${reasons[reasons.length - 1]}.`;
  return `Eine Vollzeit-Beschäftigung wäre derzeit nicht zumutbar im Sinne des § 10 Abs. 1 SGB II. Begründung: ${reasonText}`;
}

function buildRiskIndicators(ctx: HeuristicsContext): string {
  const lines = collectRiskBullets(ctx);
  return joinBullets(
    lines.map(bullet),
    'Keine eindeutigen Risikoindikatoren aus den strukturierten Daten ableitbar — bitte fachlich ergänzen.',
  );
}

function buildStrengthIndicators(ctx: HeuristicsContext): string {
  const lines = collectStrengthBullets(ctx);
  return joinBullets(
    lines.map(bullet),
    'Keine eindeutigen Stärken aus den strukturierten Daten ableitbar — bitte fachlich ergänzen.',
  );
}

function collectRiskBullets(ctx: HeuristicsContext): string[] {
  const out: string[] = [];

  // Score-Engine-Risikoindikatoren übernehmen (mit Score-Engine-Texten)
  for (const ri of ctx.input.score.riskIndicators) {
    out.push(ri.message);
  }

  // BG-Lücke — wenn nicht schon über Score-Indikator drin
  if (
    ctx.bgGapCents > 0 &&
    !ctx.input.score.riskIndicators.some((r) => r.code === 'private_bg_gap_large')
  ) {
    out.push(
      `Tragfähigkeitslücke ${formatEuro(ctx.bgGapCents as MoneyCents)}/Monat (BG-Bedarf vs. Gewinn).`,
    );
  }

  // Stundensatz unter Branchenuntergrenze (heuristisch über business.branch)
  if (
    ctx.hourlyRateEuro !== null &&
    ctx.hourlyRateEuro > 0 &&
    ctx.hourlyRateEuro < ctx.branchFloorEuro &&
    !ctx.input.score.riskIndicators.some((r) => r.code === 'style_hourly_rate_below_floor')
  ) {
    const gap = Math.round((1 - ctx.hourlyRateEuro / ctx.branchFloorEuro) * 100);
    out.push(
      `Bereinigter Stundensatz ${formatEuroPerHour(ctx.hourlyRateEuro)} liegt ${formatPercent(gap)} unter der Branchenuntergrenze von ${formatEuroPerHour(ctx.branchFloorEuro)}.`,
    );
  }

  // Klumpenrisiko (wenn vorhanden und nicht über Score-Indikator drin)
  if (
    ctx.maxClumpenSharePct > 50 &&
    !ctx.input.score.riskIndicators.some((r) => r.code === 'activity_klumpenrisiko')
  ) {
    out.push(
      `Klumpenrisiko: ein Auftraggeber stellt ${formatPercent(ctx.maxClumpenSharePct)} des Umsatzes.`,
    );
  }

  // Hohe Umsatzschwankung
  if (ctx.hasBwa && ctx.months >= 3 && ctx.revenueCv > 0.3) {
    out.push(
      `Hohe Umsatzschwankung von ${formatPercent(ctx.revenueCv * 100)} (über ${String(ctx.months)} Monate); Saisonalität / Auftragsstruktur prüfen.`,
    );
  }

  return out;
}

function collectStrengthBullets(ctx: HeuristicsContext): string[] {
  const out: string[] = [];

  // Stabile Umsätze
  if (ctx.hasBwa && ctx.months >= 6 && ctx.revenueCv < 0.15) {
    out.push(`Stabile Umsätze über ${String(ctx.months)} Monate (Schwankung unter 15 %).`);
  }

  // Lange Geschäftshistorie
  if (ctx.businessAgeMonths >= 24) {
    const years = Math.floor(ctx.businessAgeMonths / 12);
    out.push(`Etabliertes Geschäft seit über ${String(years)} Jahren.`);
  }

  // Branchenrelevante Qualifikation
  if (ctx.identity?.qualifications.some((q) => q.industryRelevant)) {
    const titles = ctx.identity.qualifications
      .filter((q) => q.industryRelevant)
      .map((q) => q.title)
      .join(', ');
    out.push(`Branchenrelevante Qualifikation dokumentiert: ${titles}.`);
  }

  // Unternehmerische Erfahrung
  if (ctx.identity?.hasEntrepreneurialExperience) {
    out.push('Vorhandene unternehmerische Vorerfahrung.');
  }

  // Wiederkehrende Umsätze
  if (ctx.activity?.hasRecurringRevenue) {
    out.push('Wiederkehrende Umsätze sichern Grundauslastung.');
  }

  // Skalierbarkeit
  if (ctx.activity?.scalability === 'highly_scalable') {
    out.push('Hoch skalierbares Geschäftsmodell.');
  }

  // Klar definierte Zielgruppe
  if (ctx.style?.targetGroupDefined) {
    out.push('Klar umrissene Zielgruppe.');
  }

  // Pricing-Strategie dokumentiert
  if (ctx.style?.pricingStrategyDocumented) {
    out.push('Dokumentierte Pricing-Strategie.');
  }

  // Komfortable Liquidität
  if (ctx.runwayWeeks !== null && ctx.runwayWeeks >= 12) {
    out.push(`Komfortable Liquiditätsreichweite von rund ${String(ctx.runwayWeeks)} Wochen.`);
  }

  // Positiver Cashflow
  if (ctx.hasBwa && ctx.avgProfitCents > 0 && ctx.bgCoveragePct >= 100) {
    out.push(`Gewinn deckt den BG-Bedarf vollständig (${formatPercent(ctx.bgCoveragePct)}).`);
  }

  // Belastbarkeit (hoch)
  if (
    ctx.health?.selfReportedResilienceScore !== undefined &&
    ctx.health.selfReportedResilienceScore >= 7
  ) {
    out.push(
      `Selbsteingeschätzte Belastbarkeit hoch (${String(ctx.health.selfReportedResilienceScore)}/10).`,
    );
  }

  return out;
}

function buildConditionsParagraph(ctx: HeuristicsContext): string {
  if (ctx.traffic !== 'yellow') return '';
  const items: string[] = [
    'monatliche Übermittlung der BWA an die fachkundige Stelle',
    'Vorlage eines Liquiditätsplans für die nächsten zwölf Monate',
  ];
  if (ctx.utilizationPct !== null && ctx.utilizationPct < 60) {
    items.push('Nachweis konkreter Akquise-Aktivitäten');
  }
  if (
    ctx.hourlyRateEuro !== null &&
    ctx.hourlyRateEuro > 0 &&
    ctx.hourlyRateEuro < ctx.branchFloorEuro
  ) {
    items.push(
      `Anhebung des Stundensatzes in Richtung Branchenuntergrenze (${formatEuroPerHour(ctx.branchFloorEuro)})`,
    );
  }
  items.push('Re-Evaluation in sechs Monaten');
  const list = items.map((s) => `· ${s}`).join('\n');
  return `Die Fortführung wird unter folgenden Auflagen empfohlen:\n${list}`;
}

function buildReevaluationProposal(ctx: HeuristicsContext): string {
  if (ctx.traffic === 'green') return '';
  const monthsAhead = ctx.traffic === 'red' ? 3 : 6;
  const date = addMonths(ctx.currentDate, monthsAhead);
  const docs =
    ctx.traffic === 'red'
      ? 'aktualisierte BWA und Nachweis der Vermittlungsbemühungen'
      : 'aktualisierte BWA, EKS und Auftragsbestand';
  return `Re-Evaluation am ${formatGermanDate(date)} mit ${docs}.`;
}

// ---------------------------------------------------------------------------
// Pro Dimension: short + detail
// ---------------------------------------------------------------------------

function buildDimensionTexts(
  ctx: HeuristicsContext,
): Readonly<Record<DimensionKey, DimensionTextPair>> {
  return {
    identity: buildIdentityText(ctx),
    activity: buildActivityText(ctx),
    style: buildStyleText(ctx),
    resources: buildResourcesText(ctx),
    health: buildHealthText(ctx),
    private: buildPrivateText(ctx),
  };
}

function getDimensionScoreEntry(
  ctx: HeuristicsContext,
  key: DimensionKey,
): { score: number; confidence: number } | null {
  const e = ctx.input.score.dimensionScores.find((d) => d.dimension === key);
  if (!e) return null;
  return { score: e.score, confidence: e.confidencePercent };
}

function dimensionShortByScore(score: number, label: string): string {
  if (score >= 70) return `${label}: tragfähig.`;
  if (score >= 40) return `${label}: tragfähig mit Einschränkungen.`;
  return `${label}: kritischer Befund.`;
}

function buildIdentityText(ctx: HeuristicsContext): DimensionTextPair {
  const entry = getDimensionScoreEntry(ctx, 'identity');
  const id = ctx.identity;
  if (!entry || !id) {
    return {
      short: 'Identität & Qualifikation: Datenlage unzureichend.',
      detail:
        'Vorschlag nicht möglich — keine strukturierten Daten zur Dimension Identität & Qualifikation erfasst.',
    };
  }
  const sentences: string[] = [];
  const industryQuals = id.qualifications.filter((q) => q.industryRelevant);
  if (industryQuals.length > 0) {
    sentences.push(
      `Die Mandantin/der Mandant verfügt über branchenrelevante Qualifikation (${industryQuals.map((q) => q.title).join(', ')}).`,
    );
  } else if (id.qualifications.length > 0) {
    sentences.push(
      `Vorhandene Qualifikationen sind nicht ausdrücklich als branchenrelevant ausgewiesen.`,
    );
  } else {
    sentences.push('Keine Qualifikationen erfasst.');
  }
  if (id.hasEntrepreneurialExperience) {
    sentences.push(
      typeof id.yearsSelfEmployed === 'number'
        ? `Unternehmerische Erfahrung von ${String(id.yearsSelfEmployed)} Jahren liegt vor.`
        : 'Unternehmerische Vorerfahrung dokumentiert.',
    );
  } else {
    sentences.push(
      'Keine vorherige unternehmerische Erfahrung — kaufmännische Kompetenzen prüfen.',
    );
  }
  if (id.personalTraits.length >= 2) {
    sentences.push('Persönliche Eignung in mehreren Dimensionen dokumentiert.');
  }
  return {
    short: dimensionShortByScore(entry.score, 'Identität & Qualifikation'),
    detail: sentences.join(' '),
  };
}

function buildActivityText(ctx: HeuristicsContext): DimensionTextPair {
  const entry = getDimensionScoreEntry(ctx, 'activity');
  const act = ctx.activity;
  if (!entry || !act) {
    return {
      short: 'Aktivität & Geschäftsmodell: Datenlage unzureichend.',
      detail:
        'Vorschlag nicht möglich — keine strukturierten Daten zur Dimension Aktivität & Geschäftsmodell erfasst.',
    };
  }
  const sentences: string[] = [];
  if (act.valueProposition && act.valueProposition.trim().length > 0) {
    sentences.push('Das Wertversprechen ist im Wizard hinterlegt.');
  }
  if (ctx.maxClumpenSharePct > 50) {
    sentences.push(
      `Klumpenrisiko: ein Auftraggeber stellt ${formatPercent(ctx.maxClumpenSharePct)} des Umsatzes.`,
    );
  } else if (act.topClientsByRevenue.length > 0) {
    sentences.push('Auftraggeber-Struktur ist diversifiziert.');
  }
  if (act.hasRecurringRevenue) {
    sentences.push('Wiederkehrende Umsätze sichern eine Grundauslastung.');
  } else {
    sentences.push(
      'Keine wiederkehrenden Umsätze — Tragfähigkeit hängt an kontinuierlicher Neuakquise.',
    );
  }
  switch (act.scalability) {
    case 'highly_scalable':
      sentences.push('Skalierbarkeit hoch.');
      break;
    case 'partly_scalable':
      sentences.push('Skalierbarkeit teilweise gegeben.');
      break;
    case 'linear':
      sentences.push(
        'Skalierbarkeit gering — Wachstum nur über Stundensatz oder zusätzliche Personen.',
      );
      break;
    case 'unknown':
      break;
  }
  return {
    short: dimensionShortByScore(entry.score, 'Geschäftsmodell'),
    detail: sentences.join(' '),
  };
}

function buildStyleText(ctx: HeuristicsContext): DimensionTextPair {
  const entry = getDimensionScoreEntry(ctx, 'style');
  const st = ctx.style;
  if (!entry || !st) {
    return {
      short: 'Stil & Marktposition: Datenlage unzureichend.',
      detail:
        'Vorschlag nicht möglich — keine strukturierten Daten zur Dimension Stil & Marktposition erfasst.',
    };
  }
  const sentences: string[] = [];
  if (st.targetGroupDefined) {
    sentences.push('Die Zielgruppe ist klar umrissen.');
  } else {
    sentences.push('Die Zielgruppe ist nicht klar definiert — Akquise und Pricing erschwert.');
  }
  if (st.competitors.length > 0) {
    sentences.push(
      `Wettbewerbsumfeld erfasst (${String(st.competitors.length)} Wettbewerber dokumentiert).`,
    );
  }
  if (
    typeof st.selfReportedHourlyRateEuro === 'number' &&
    typeof st.industryFloorHourlyRateEuro === 'number' &&
    st.industryFloorHourlyRateEuro > 0
  ) {
    if (st.selfReportedHourlyRateEuro < st.industryFloorHourlyRateEuro) {
      sentences.push(
        `Stundensatz ${formatEuroPerHour(st.selfReportedHourlyRateEuro)} liegt unter der Branchenuntergrenze von ${formatEuroPerHour(st.industryFloorHourlyRateEuro)}.`,
      );
    } else {
      sentences.push(
        `Stundensatz ${formatEuroPerHour(st.selfReportedHourlyRateEuro)} entspricht der Branchenuntergrenze oder liegt darüber.`,
      );
    }
  }
  if (st.pricingStrategyDocumented) {
    sentences.push('Pricing-Strategie dokumentiert.');
  } else {
    sentences.push('Keine Pricing-Strategie dokumentiert.');
  }
  return {
    short: dimensionShortByScore(entry.score, 'Marktposition'),
    detail: sentences.join(' '),
  };
}

function buildResourcesText(ctx: HeuristicsContext): DimensionTextPair {
  const entry = getDimensionScoreEntry(ctx, 'resources');
  if (!entry) {
    return {
      short: 'Ressourcen & Finanzen: Datenlage unzureichend.',
      detail:
        'Vorschlag nicht möglich — Score zur Dimension Ressourcen & Finanzen wurde nicht berechnet.',
    };
  }
  const sentences: string[] = [];
  if (ctx.hasBwa) {
    sentences.push(
      `Durchschnittlicher Monatsgewinn ${formatEuro(ctx.avgProfitCents as MoneyCents)} bei Umsatz ${formatEuro(ctx.avgRevenueCents as MoneyCents)} und Kosten ${formatEuro(ctx.avgCostsCents as MoneyCents)}.`,
    );
  } else {
    sentences.push('Wirtschaftliche Kennzahlen liegen nicht in ausreichender Tiefe vor.');
  }
  if (ctx.liquidityCents !== null) {
    if (ctx.runwayWeeks !== null && ctx.runwayWeeks > 0) {
      sentences.push(
        `Liquide Mittel ${formatEuro(ctx.liquidityCents as MoneyCents)} reichen für rund ${String(ctx.runwayWeeks)} Wochen.`,
      );
    } else {
      sentences.push(
        `Liquide Mittel ${formatEuro(ctx.liquidityCents as MoneyCents)} dokumentiert.`,
      );
    }
  }
  if (ctx.hours !== null && ctx.utilizationPct !== null) {
    sentences.push(
      `Auslastung ${String(ctx.hours)} h/Monat (${formatPercent(ctx.utilizationPct)} der Vollzeit-Referenz).`,
    );
  }
  return {
    short: dimensionShortByScore(entry.score, 'Ressourcen & Finanzen'),
    detail: sentences.join(' '),
  };
}

function buildHealthText(ctx: HeuristicsContext): DimensionTextPair {
  const entry = getDimensionScoreEntry(ctx, 'health');
  const h = ctx.health;
  if (!entry || !h) {
    return {
      short: 'Gesundheit & Resilienz: Datenlage unzureichend.',
      detail:
        'Vorschlag nicht möglich — keine strukturierten Daten zur Dimension Gesundheit & Resilienz erfasst.',
    };
  }
  const sentences: string[] = [];
  if (typeof h.selfReportedResilienceScore === 'number') {
    sentences.push(
      `Selbsteingeschätzte Belastbarkeit ${String(h.selfReportedResilienceScore)}/10.`,
    );
  }
  if (typeof h.hoursWorkedPerWeek === 'number') {
    const hpw = h.hoursWorkedPerWeek as number;
    if (hpw > 55) {
      sentences.push(
        `Wochenarbeitszeit ${String(hpw)} h überschreitet die nachhaltige Grenze von 55 h.`,
      );
    } else {
      sentences.push(`Wochenarbeitszeit ${String(hpw)} h liegt im nachhaltigen Bereich.`);
    }
  }
  if (h.conditionsAffectWork) {
    sentences.push('Gesundheitliche Einschränkung beeinträchtigt nach Eigenangabe die Tätigkeit.');
  } else if (h.chronicConditionsExist) {
    sentences.push(
      'Chronische Bedingungen bestehen, beeinflussen die Tätigkeit nach Eigenangabe jedoch nicht.',
    );
  } else {
    sentences.push('Keine gesundheitlichen Einschränkungen dokumentiert.');
  }
  return {
    short: dimensionShortByScore(entry.score, 'Gesundheit & Resilienz'),
    detail: sentences.join(' '),
  };
}

function buildPrivateText(ctx: HeuristicsContext): DimensionTextPair {
  const entry = getDimensionScoreEntry(ctx, 'private');
  if (!entry) {
    return {
      short: 'Privates Umfeld & Bedarfsgemeinschaft: Datenlage unzureichend.',
      detail:
        'Vorschlag nicht möglich — Score zur Dimension Privates Umfeld & Bedarfsgemeinschaft wurde nicht berechnet.',
    };
  }
  const sentences: string[] = [];
  const adults = ctx.input.financial.bgComposition.members.filter(
    (m) => m.role === 'antragsteller' || m.role === 'partner',
  ).length;
  const kids = ctx.input.financial.bgComposition.members.filter((m) => m.role === 'kind');
  sentences.push(
    `Bedarfsgemeinschaft mit ${String(adults)} Erwachsene${adults === 1 ? 'r/m' : 'n'}${kids.length > 0 ? ` und ${String(kids.length)} Kind${kids.length === 1 ? '' : 'ern'}` : ''}.`,
  );
  if (ctx.bgGapCents > 0) {
    sentences.push(
      `Tragfähigkeitslücke ${formatEuro(ctx.bgGapCents as MoneyCents)}/Monat — ergänzende Hilfebedürftigkeit besteht fort.`,
    );
  } else {
    sentences.push('Wirtschaftliche Bedarfsdeckung gegeben.');
  }
  if (ctx.private_) {
    if (ctx.private_.childcareStatus === 'secured') {
      sentences.push('Kinderbetreuung gesichert.');
    } else if (ctx.private_.childcareStatus === 'not_secured') {
      sentences.push('Kinderbetreuung nicht gesichert — Zumutbarkeit prüfen.');
    }
    if (ctx.private_.hasCareResponsibility) {
      sentences.push(
        'Pflegeverantwortung im Haushalt — Zumutbarkeitsgrund nach § 10 SGB II prüfen.',
      );
    }
  }
  return {
    short: dimensionShortByScore(entry.score, 'Privates Umfeld & Bedarfsgemeinschaft'),
    detail: sentences.join(' '),
  };
}
